/**
 * The Old Grove's deadfall, and the economy it adds up to.
 *
 * Split out of the scene so the numbers can be *tested* rather than restated.
 * The economy comment below has been wrong three times, and each time a test
 * would have caught it if the test had not been hand-copying the same figures
 * it was checking. Importing the scene into a test is not an option — it pulls
 * Phaser, which this package's vitest environment does not load — so the table
 * lives here, Phaser-free, and both the scene and the tests read it.
 *
 * `import type` above is load-bearing for that: it is erased at compile time,
 * so nothing here reaches Phaser at runtime.
 */
import type { ScenePropSpec } from '../ScenePropsSystem';

/**
 * Normalised centre of the stone basin, which the art already frames as the
 * focal point of the clearing. The fire sits in it. Tuned by eye against a
 * screenshot rather than derived — the basin is painted, not data.
 */
export const FIRE_NX = 0.26;
export const FIRE_NY = 0.745;

/**
 * Deadfall around the clearing — and the economy, in one table.
 *
 * Placed rather than painted in, so each one takes the perspective scale at its
 * own y and sorts against the walker. The log, the kindling and the stump are
 * what the fire is fed with, so they sit where someone would actually have to
 * walk to reach them.
 *
 * The fire only burns while there is dark to burn against, which is 420 s of
 * the 600 s vigil. Held at full that costs 4.2 fuel; left to sit near a quarter
 * it costs about 2.3.
 *
 * Against that, what the player can actually *reach* is the fire's starting
 * 0.75 plus three sources — 0.40 from the near log, 0.26 from the near
 * kindling, 0.84 from the stump — for 2.25 total. So sinks run 1.03x sources
 * if you keep the fire low and 1.87x if you keep it high. The spec's target
 * band is 1.1-1.3 (Dan Cook, "Value chains"), so greedy play is well outside
 * it and cautious play is slightly under: you cannot hold a bonfire till
 * morning, and sitting low is very nearly free.
 *
 * The log at nx 0.80 and the kindling at nx 0.925 are out of the fire's reach
 * at any size — they are the lantern's, and they only became real content the
 * moment there was a light you could carry to them.
 *
 * The lantern is stone and takes both hands, so you cannot carry it and
 * firewood at once: light out means fuel home in the dark.
 *
 * That toll is paid ONCE, not per trip, and it is meant to be. The far log and
 * the far kindling sit about 257 world px apart against a lantern reach of 392,
 * so a single placement between them lights both for the rest of the night and
 * they are then gathered with empty hands like anything else. It would be easy
 * to read that as the mechanic leaking, and an earlier version of this comment
 * called it "the only hard trade in the game", which overstated it.
 *
 * It is not a leak. The spec's economy rule is that the budget refills with
 * progress rather than rest — "Discovery grants light... Exploration is never
 * taxed" (docs/design/single-scene-game-spec.md, "The economy", after DCSS
 * deleting its food clock). One expedition that permanently opens half the
 * grove is exactly that shape. Charging a fresh toll on every return trip would
 * tax exploration, which is the one thing the doctrine forbids.
 *
 * The economy, measured against `burnFuel` itself rather than estimated, over
 * NightVigil's 420 s dark window. 2.70 gatherable here across 11 armfuls, plus
 * the fire's opening 0.75 — 3.45 all told:
 *
 *     hold the fire at   costs   sinks/sources
 *          1.00           4.20        1.22   <- in the target band
 *          0.90           3.95        1.14
 *          0.80           3.70        1.07
 *          0.70           3.45        1.00   <- richest fire affordable all night
 *          0.25           2.31        0.67
 *
 * The spec's target is sinks 1.1-1.3x sources (Dan Cook, "Value chains"), and
 * greedy play now sits at 1.22. The shape that produces: strip the entire grove
 * and you can hold a *good* fire till morning, around 0.70, but never a bonfire
 * — which is the intended feeling, and there is no way to lose either way.
 *
 * The far log went from 3 armfuls to 2 to get here. At 3 the total was 3.87 and
 * even maximum greed only reached 1.09, under the band: gather everything and
 * fuel simply stopped being scarce.
 *
 * Three earlier versions of this comment were wrong, twice by dividing by the
 * gatherable total and forgetting the fire's opening fuel is a source the player
 * spends. Recompute against the code, never against the last comment.
 *
 * The other half of Phase 3 is that fuel was never the binding constraint
 * anyway — time is. See GATHER_WORK_MS in hearthRules.
 *
 * Numbers here are the tuning surface; see docs/design/single-scene-game-spec.md
 * → Phase 3 for what they are being tuned *to*.
 */
export const GROVE_PROPS: ScenePropSpec[] = [
  { key: 'prop-fire-ring',  nx: FIRE_NX, ny: FIRE_NY + 0.005, widthFrac: 0.15 },
  { key: 'prop-lantern',    nx: 0.36,  ny: 0.655, widthFrac: 0.06 },
  // Both of these used to sit at nx 0.115 / 0.055, which put them entirely
  // inside the near trunk's footprint (screen x -176..287) once that trunk was
  // grown to leave the frame. They were not occluded, they were invisible —
  // the log's moss-and-rings texture and a whole fern erased from a scene whose
  // job is to be dense and worth looking at. Moved clear, with a little overlap
  // kept so they read as standing BEHIND the trunk rather than beside it.
  // Near the hearth: picked over already, so cheap and quick but thin.
  { key: 'prop-fallen-log', nx: 0.215, ny: 0.735, widthFrac: 0.115, fuel: 0.20, armfuls: 2 },
  { key: 'prop-fern',       nx: 0.185, ny: 0.655, widthFrac: 0.055 },
  { key: 'prop-kindling',   nx: 0.325, ny: 0.815, widthFrac: 0.075, fuel: 0.13, armfuls: 2 },
  { key: 'prop-grass',      nx: 0.44,  ny: 0.845, widthFrac: 0.055 },
  { key: 'prop-stones',     nx: 0.50,  ny: 0.695, widthFrac: 0.05 },
  // the far half of the world, so walking east is worth doing
  { key: 'prop-stump',      nx: 0.615, ny: 0.675, widthFrac: 0.085, fuel: 0.28, armfuls: 3 },
  { key: 'prop-fern',       nx: 0.695, ny: 0.79,  widthFrac: 0.085, flip: true },
  { key: 'prop-fallen-log', nx: 0.80,  ny: 0.70,  widthFrac: 0.13, flip: true, fuel: 0.42, armfuls: 2 },
  { key: 'prop-grass',      nx: 0.755, ny: 0.855, widthFrac: 0.055, flip: true },
  { key: 'prop-stones',     nx: 0.885, ny: 0.735, widthFrac: 0.06 },
  { key: 'prop-kindling',   nx: 0.925, ny: 0.80,  widthFrac: 0.07, flip: true, fuel: 0.18, armfuls: 2 },
  { key: 'prop-fern',       nx: 0.955, ny: 0.655, widthFrac: 0.07 },
];

/** Total fuel the clearing holds, across every armful of every source. */
export function totalGatherableFuel(): number {
  return GROVE_PROPS.reduce((sum, p) => sum + (p.fuel ?? 0) * (p.armfuls ?? 1), 0);
}

/** How many armfuls there are to work loose in the whole grove. */
export function totalArmfuls(): number {
  return GROVE_PROPS.reduce((sum, p) => sum + (p.fuel ? (p.armfuls ?? 1) : 0), 0);
}
